import type { AlertEvent } from '@viaja/contracts';
import { AlertCard, Button, EmptyState, PushNotification } from '@viaja/design-system';
import { firstName } from '@viaja/domain';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatCheckedAt, money, routeDates } from '../../lib/format';
import { useAlerts, useMe, useUpdateRoute } from '../../lib/queries';
import { MainScreen } from '../../ui/AppShell';
import { LoadError, Loading } from '../../ui/feedback';
import { TelegramCard } from './TelegramCard';

const KIND_TITLE: Record<AlertEvent['kind'], string> = {
  target_hit: 'O preço chegou',
  rare_deal: 'Oportunidade rara',
  near_target: 'Perto do alvo',
  sharp_drop: 'O preço caiu',
};

const RECENT_MS = 48 * 3_600_000;

/** Alertas: o último alvo atingido como AlertCard (até 48 h), o resto no formato do PushNotification. */
export function AlertsPage() {
  const alerts = useAlerts();
  const navigate = useNavigate();

  return (
    <MainScreen>
      {alerts.isPending ? (
        <Loading label="Buscando seus alertas…" />
      ) : alerts.error ? (
        <LoadError message={alerts.error.message} onRetry={() => alerts.refetch()} />
      ) : (
        <>
          <div className="app-head">
            <h1 className="display-title">Alertas</h1>
          </div>
          {alerts.data.length === 0 ? (
            <EmptyState
              pose="aguardar"
              title="Nenhum alerta ainda"
              body="Quando um preço chegar ao seu alvo ou cair de repente, o aviso aparece aqui."
              actionLabel="Ver suas rotas"
              mascotSize={160}
              onAction={() => navigate('/')}
            />
          ) : (
            <AlertList alerts={alerts.data} />
          )}
          <section className="app-section" aria-labelledby="telegram">
            <h2 id="telegram" className="display-section">
              Telegram
            </h2>
            <TelegramCard />
          </section>
        </>
      )}
    </MainScreen>
  );
}

function AlertList({ alerts }: { alerts: AlertEvent[] }) {
  const [head, ...rest] = alerts;
  const [dismissed, setDismissed] = useState(false);
  // O AlertCard do DS sempre diz "Alvo atingido": só serve para esse tipo de alerta.
  const recent = Boolean(
    head && head.kind === 'target_hit' && !dismissed && Date.now() - Date.parse(head.createdAt) < RECENT_MS,
  );
  return (
    <div className="app-stack">
      {head && recent ? <LatestAlert alert={head} onDismiss={() => setDismissed(true)} /> : null}
      <ul className="app-list" aria-label="Alertas anteriores">
        {(recent ? rest : alerts).map((a) => (
          <li key={a.id}>
            <Link to={`/rotas/${a.routeId}`} className="app-cardlink">
              <PushNotification
                title={`${KIND_TITLE[a.kind]}: ${a.route.originCity} → ${a.route.destinationCity}`}
                body={`${money(a.price)}${a.target ? ` · alvo ${money(a.target)}` : ''} · ${routeDates(a.route.outboundDate, a.route.returnDate)}`}
                time={formatCheckedAt(a.createdAt)}
              />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LatestAlert({ alert, onDismiss }: { alert: AlertEvent; onDismiss: () => void }) {
  const me = useMe();
  const navigate = useNavigate();
  const update = useUpdateRoute(alert.routeId);
  return (
    <>
      <AlertCard
        origin={alert.route.originCity}
        destination={alert.route.destinationCity}
        dates={routeDates(alert.route.outboundDate, alert.route.returnDate)}
        price={alert.price}
        {...(alert.belowTypicalPct ? { belowTypical: alert.belowTypicalPct } : {})}
        {...(alert.target ? { target: alert.target } : {})}
        time={formatCheckedAt(alert.createdAt)}
        {...(alert.channels.includes('telegram') ? { channel: 'Telegram' } : {})}
        name={firstName(me.data?.name ?? 'Cris')}
        actionLabel="Ver a rota"
        onBuy={() => navigate(`/rotas/${alert.routeId}`)}
        onSnooze={onDismiss}
      />
      {update.error ? <p className="caption app-field-error">{update.error.message}</p> : null}
      <Button variant="quiet" size="sm" onClick={() => update.mutate({ alertEnabled: false })}>
        Pausar alerta dessa rota
      </Button>
    </>
  );
}
